/**
 * Map ER-10860 pay-plans-list row → PayPlansTable presentational row.
 */

import { formatPayRangeAmount } from "@components/utils/formatPayRangeAmount";
import { resolveReadinessEnabled } from "@components/utils/useReadinessEnabled";
import { formatPositionCode } from "@components/PositionsPayRangesPage/mapPositionSubscriptionRow";
import {
  formatFairPayDate,
  formatFairPayVersionCode,
  normalizeFairPayVersionLabel,
} from "@components/utils/fairPayFormat";

const TIMELINE_VARIANT = {
  NoCurrent: "no-current",
  Current: "current",
  "Past-Current": "past-current",
  "Current-Future": "current-future",
  "Past-Current-Future": "past-current-future",
  "NoCurrent-Future": "no-current-future",
  "Past-NoCurrent": "past-no-current",
};

const VARIANT_BUTTON = {
  "no-current": "propose-first-pay-plan",
  "past-no-current": "propose-pay-plan",
  "no-current-future": "propose-pay-plan",
  current: "propose-changes",
  "current-future": "propose-changes",
  "past-current": "propose-changes",
  "past-current-future": "propose-changes",
};

// Figma labels that differ from the button's default copy.
const BUTTON_LABEL = {
  "past-no-current": "Add Pay Plan Proposal",
};

export function timelineStateToVariant(state) {
  return TIMELINE_VARIANT[state] || "no-current";
}

/**
 * Period state vs today for the bar colour (Eric, 2026-10-14): green = current,
 * blue = future, grey = lapsed. Derived from the row's timeline state.
 */
function planPeriodState(timelineState) {
  const segments = String(timelineState || "").split("-");
  if (segments.includes("Current")) return "current";
  if (segments.includes("Future")) return "future";
  if (segments.includes("Past")) return "lapsed";
  return "current";
}

function formatPlanAmount(plan) {
  if (plan?.amount == null || plan.amount === "") return "";
  const compact = formatPayRangeAmount(plan.amount).replace(/\.0(?=[KMB]|$)/, "");
  if (!compact || compact === "-") return "";
  return `${plan.currency_symbol || "$"}${compact}`;
}

function mapCurrentPayPlan(timeline, plan) {
  const variant = timelineStateToVariant(timeline?.state);
  const startDate = formatFairPayDate(plan?.effective_on);
  const endDate = plan?.effective_by
    ? formatFairPayDate(plan.effective_by)
    : startDate
      ? "present"
      : "";
  return {
    variant,
    state: planPeriodState(timeline?.state),
    amountFormatted: formatPlanAmount(plan),
    basisLabel: plan?.currency_code ? `${plan.currency_code}/year` : "",
    dateRange: "",
    startDate,
    endDate,
    effectiveOn: plan?.effective_on || null,
    effectiveBy: plan?.effective_by || null,
    planReference: plan?.code || "",
    versionLabel: plan?.version_number ? `V${plan.version_number}` : "",
    // The slider opens the clicked version's drawer with this id.
    versionId: plan?.version_id ?? null,
    status: plan?.status || null,
  };
}

/** API period state (`PayPlanVersion.STATE_*`) → the card's colour token. */
const VERSION_STATE = {
  CURRENT: "current",
  FUTURE: "future",
  LAPSED: "lapsed",
};

/** Map one `pay_plan_versions` entry (ER-12368) onto the card's props. */
export function mapPlanVersion(version) {
  const state = VERSION_STATE[String(version?.state || "").toUpperCase()] || "current";
  const startDate = formatFairPayDate(version?.effective_on);
  return {
    variant: "current",
    state,
    amountFormatted: formatPlanAmount(version),
    basisLabel: version?.currency_code ? `${version.currency_code}/year` : "",
    startDate,
    endDate: version?.effective_by
      ? formatFairPayDate(version.effective_by)
      : startDate
        ? "present"
        : "",
    effectiveOn: version?.effective_on || null,
    effectiveBy: version?.effective_by || null,
    planReference: version?.code || "",
    versionLabel: version?.version_number ? `V${version.version_number}` : "",
    versionId: version?.version_id ?? version?.id ?? null,
    status: version?.status || null,
  };
}

/**
 * Trailing slider entry for an employee whose plans have all lapsed (no plan in
 * force on the as-of date): the "Add new Pay Plan" card, so ‹ steps back to the
 * lapsed plan and › returns to it (ER-12676).
 */
function mapAddPlaceholder(variant) {
  return {
    variant,
    state: "lapsed",
    amountFormatted: "",
    basisLabel: "",
    startDate: "",
    endDate: "",
    effectiveOn: null,
    effectiveBy: null,
    planReference: "",
    versionLabel: "",
    versionId: null,
    status: null,
    isAddPlaceholder: true,
  };
}

/**
 * The slider's versions (oldest → newest). ER-12368 adds `pay_plan_versions` to
 * the list row; until it lands we fall back to the single current version so the
 * cell renders exactly as before.
 */
function mapPayPlanVersions(apiRow, timeline) {
  const list = apiRow?.pay_plan_versions;
  if (Array.isArray(list) && list.length) {
    const versions = list.map(mapPlanVersion);
    const variant = timelineStateToVariant(timeline?.state);
    return variant === "past-no-current"
      ? [...versions, mapAddPlaceholder(variant)]
      : versions;
  }
  const current = mapCurrentPayPlan(timeline, apiRow?.current_pay_plan);
  return current ? [current] : [];
}

const PROPOSAL_CARD_STATUS = {
  DRAFT: "Draft",
  PENDING: "PendingApprovals",
  APPROVED: "PendingReconciliation",
  PENDING_EXECUTION: "PendingReconciliation",
  REJECTED: "Rejected",
};

function formatProposalAmountLine(apiChanges) {
  const amount = formatPlanAmount({
    amount: apiChanges?.proposed_amount,
    currency_symbol: apiChanges?.currency_symbol,
    currency_code: apiChanges?.currency_code,
  });
  const code = apiChanges?.currency_code;
  if (amount && code) return `${code} · ${amount} · Annual`;
  if (amount) return amount;
  if (apiChanges?.code) return apiChanges.code;
  return "Draft proposal";
}

function mapProposalCard(apiChanges, { rejected = false } = {}) {
  return {
    mode: "proposal",
    status: rejected
      ? "Rejected"
      : PROPOSAL_CARD_STATUS[apiChanges?.status] || "Draft",
    date: formatFairPayDate(apiChanges?.effective_date),
    amount: formatProposalAmountLine(apiChanges),
    showClose: false,
    recordId: apiChanges?.proposal,
  };
}

/** Tooltip copy per `blocked_reason` (ER-12690); `{code}` / `{date}` are filled by the mapper. */
const BLOCKED_COPY = {
  pending_proposal: {
    title: "A proposal is already open",
    description: ({ code }) => `${code} is waiting for approval. Click to open it.`,
  },
  future_plan_committed: {
    title: "A future pay plan is already committed",
    description: ({ code, date }) =>
      `${code} starts${date ? ` ${date}` : ""}. Void it from its menu to propose a different change. Click to open it.`,
  },
  first_plan_proposal_pending: {
    title: "A first pay plan proposal is open",
    description: ({ code }) =>
      `Add Pay Plan is blocked until ${code} is committed or voided. Click to open it.`,
  },
};

const BLOCKED_CODE_FALLBACK = {
  pending_proposal: "The proposal",
  future_plan_committed: "The future pay plan",
  first_plan_proposal_pending: "The first pay plan proposal",
};

/**
 * The API reports why Add / Propose is not allowed (`blocked_reason`) and which
 * version blocks it (`blocking_pay_plan_version_id`). Null when not blocked or
 * the reason is unknown.
 */
export function mapBlockedReason(apiRow) {
  const reason = apiRow?.blocked_reason;
  const copy = BLOCKED_COPY[reason];
  if (!copy) return null;

  const versionId = apiRow?.blocking_pay_plan_version_id ?? null;
  const changes = apiRow?.pay_plan_changes;
  const blockingVersion =
    versionId == null
      ? null
      : (apiRow?.pay_plan_versions || []).find(
          (version) => String(version?.version_id ?? version?.id) === String(versionId)
        );

  const code = blockingVersion?.code
    ? formatFairPayVersionCode(blockingVersion.code, blockingVersion.version_number)
    : normalizeFairPayVersionLabel(changes?.code) || BLOCKED_CODE_FALLBACK[reason];
  const date = formatFairPayDate(blockingVersion?.effective_on || changes?.effective_date);

  return {
    reason,
    title: copy.title,
    description: copy.description({ code, date }),
    versionId,
    proposalId: changes?.proposal ?? null,
  };
}

function mapChanges(apiChanges, variant, apiReportsBlock = false, blocked = null) {
  const state = apiChanges?.state || "no-data";

  if (state === "in-flight-proposal") {
    return mapProposalCard(apiChanges);
  }
  if (state === "in-flight-evaluation") {
    return {
      mode: "not-eligible",
      date: formatFairPayDate(apiChanges.effective_date),
      showClose: false,
      recordId: apiChanges.evaluation,
    };
  }
  if (state === "rejected-proposal") {
    return mapProposalCard(apiChanges, { rejected: true });
  }

  const button = VARIANT_BUTTON[variant] || "propose-first-pay-plan";

  // Rule B-18 (legacy, API without `blocked_reason`): while a future plan exists
  // the Propose Changes button is disabled with an EXTooltip. When the API
  // reports the block itself, that wins over the timeline inference (ER-12691).
  const proposeChangesBlocked = apiReportsBlock
    ? Boolean(blocked) && button === "propose-changes"
    : button === "propose-changes" && /future/.test(variant);

  return {
    mode: "buttons",
    button,
    ...(BUTTON_LABEL[variant] && { buttonLabel: BUTTON_LABEL[variant] }),
    showClose: false,
    proposeChangesBlocked,
    nextEvalDate: formatFairPayDate(apiChanges?.next_adjustment_on),
  };
}

function mapFairPayStatus(apiStatus) {
  if (!apiStatus?.status) return null;
  return {
    status: apiStatus.status,
    amount: apiStatus.difference_amount ?? apiStatus.amount ?? null,
    currencyCode: apiStatus.currency_code || apiStatus.currencyCode || "USD",
    readinessPct:
      apiStatus.readiness_percentage ?? apiStatus.readinessPct ?? null,
  };
}

function departmentName(apiRow) {
  const department = apiRow?.primary_position?.department;
  const name = typeof department === "string" ? department : department?.name;
  return name || "";
}

export function hasEmployeeIdentity(row) {
  const employee = row?.employee || {};
  return Boolean(employee.id || employee.firstName || employee.lastName);
}

export function mapPayPlanListRow(apiRow) {
  const timeline = apiRow?.pay_plan_timeline || null;
  const variant = timelineStateToVariant(timeline?.state);
  const apiReportsBlock = Boolean(apiRow) && "blocked_reason" in apiRow;
  const blocked = apiReportsBlock ? mapBlockedReason(apiRow) : null;
  const firstName = apiRow?.first_name || "";
  const lastName = apiRow?.last_name || "";
  const readinessEnabled = resolveReadinessEnabled(apiRow);

  return {
    id: apiRow?.id,
    department: apiRow?.department || apiRow?.primary_position?.department || "",
    departmentId: apiRow?.department_id || apiRow?.primary_position?.department_id || null,
    employee: {
      id: apiRow?.id,
      firstName,
      lastName,
      positionTitle: apiRow?.primary_position?.name || "",
      department: departmentName(apiRow),
      avatarUrl: apiRow?.avatar || null,
      inactive: apiRow?.current_status && apiRow.current_status !== "active",
    },
    primaryPosition: {
      id:
        apiRow?.primary_position?.position_id ??
        apiRow?.primary_position?.id ??
        null,
      code: formatPositionCode(
        apiRow?.primary_position?.code_str || apiRow?.primary_position?.code
      ),
      name: apiRow?.primary_position?.name || "",
    },
    payPlan: apiRow?.current_pay_plan || null,
    currentPayPlan: mapCurrentPayPlan(timeline, apiRow?.current_pay_plan),
    payPlanVersions: mapPayPlanVersions(apiRow, timeline),
    changes: mapChanges(apiRow?.pay_plan_changes, variant, apiReportsBlock, blocked),
    blocked,
    // Raw approval lifecycle of the in-flight proposal (DRAFT / PENDING /
    // APPROVED / PENDING_EXECUTION / REJECTED) — the drawer needs it to decide
    // whether draft-only actions apply.
    proposalApprovalStatus: apiRow?.pay_plan_changes?.status || null,
    // Rule A-40: readiness off hides the Fair Pay status and shows the range penetration.
    readinessEnabled,
    fairPayStatus: readinessEnabled ? mapFairPayStatus(apiRow?.fair_pay_status) : null,
    rangePenetration: readinessEnabled ? null : apiRow?.range_penetration || null,
    currencySymbol: apiRow?.current_pay_plan?.currency_symbol || "$",
  };
}
